import m from '../m.js'
import { issueHref } from '../autolinks.js'
import { language, timeAgo } from '../i18n.js'
import { icon } from '../icons.js'
import { avatarView, labelView } from './widgets.js'

const SAME_GESTURE_MS = 60_000

/**
 * Turns GitHub's events into timeline rows: label changes by one person within a
 * minute become one row, and swapping lane labels becomes "moved from A to B".
 */
export function timelineRows(events, config) {
  const laneOf = new Map(config.lanes.map(l => [l.label, l.name]))
  const rows = []
  for (const change of events) {
    const last = rows.at(-1)
    const isLabel = change.kind === 'labeled' || change.kind === 'unlabeled'
    if (
      isLabel &&
      last?.kind === 'labels' &&
      last.actor?.login === change.actor?.login &&
      new Date(change.at) - new Date(last.at) < SAME_GESTURE_MS
    ) {
      last.changes.push(change)
      continue
    }
    rows.push(
      isLabel
        ? {
            kind: 'labels',
            at: change.at,
            actor: change.actor,
            changes: [change],
          }
        : change
    )
  }
  return rows.flatMap(row =>
    row.kind === 'labels' ? labelRows(row, laneOf) : [row]
  )
}

/**
 * Net effect of a group of label changes: a label added and removed again (or the
 * other way round) did not change anything. Lane labels become a move.
 */
function labelRows(row, laneOf) {
  const first = new Map()
  const last = new Map()
  for (const change of row.changes) {
    if (!first.has(change.label.name)) first.set(change.label.name, change)
    last.set(change.label.name, change)
  }
  const added = []
  const removed = []
  for (const [name, change] of last) {
    if (change.kind !== first.get(name).kind) continue
    ;(change.kind === 'labeled' ? added : removed).push(change.label)
  }
  const to = added.find(l => laneOf.has(l.name))
  const from = removed.find(l => laneOf.has(l.name))
  const out = []
  if (to || from)
    out.push({
      kind: 'moved',
      at: row.at,
      actor: row.actor,
      from: from && laneOf.get(from.name),
      to: to && laneOf.get(to.name),
    })
  const others = {
    added: added.filter(l => !laneOf.has(l.name)),
    removed: removed.filter(l => !laneOf.has(l.name)),
  }
  if (others.added.length || others.removed.length)
    out.push({ kind: 'labels', at: row.at, actor: row.actor, ...others })
  return out
}

/** A translated sentence with vnodes in it: placeholders become markers, then get replaced. */
function phrase(key, parts) {
  const markers = Object.fromEntries(
    Object.keys(parts).map(k => [k, `\u0001${k}\u0001`])
  )
  return m
    .t(key, markers)
    .split('\u0001')
    .map((piece, i) => (i % 2 ? parts[piece] : piece))
}

function strong(text) {
  return m('strong', text)
}
function pills(labels) {
  return m(
    'span.event-labels',
    labels.map(label => labelView({ label }))
  )
}

function sourceLink(source) {
  const text = `${source.repo ? source.repo : ''}#${source.number} ${source.title}`
  if (source.repo) return m('a', { href: source.url, target: '_blank' }, text)
  return m('a', { href: issueHref(source.number) }, text)
}

const CLOSED = {
  COMPLETED: 'event.closedCompleted',
  NOT_PLANNED: 'event.closedNotPlanned',
}
const REVIEWED = {
  APPROVED: 'event.approved',
  CHANGES_REQUESTED: 'event.changesRequested',
  DISMISSED: 'event.reviewDismissed',
}

/** (Un)assignment, phrased differently when someone assigned themselves. */
function assignment(kind) {
  return (row, actor) => {
    const toSelf = row.user && row.actor && row.user.login === row.actor.login
    return [
      'person',
      '',
      toSelf
        ? phrase(`event.${kind}Self`, { actor })
        : phrase(`event.${kind}`, { actor, user: strong(row.user?.login) }),
    ]
  }
}

function labelsPhrase(row, actor) {
  const parts = { actor, added: pills(row.added), removed: pills(row.removed) }
  if (row.added.length && row.removed.length)
    return phrase('event.labelsChanged', parts)
  return phrase(
    row.added.length ? 'event.labelsAdded' : 'event.labelsRemoved',
    parts
  )
}

function movedPhrase(row, actor) {
  const from = strong(row.from)
  const to = strong(row.to)
  if (row.from && row.to) return phrase('event.moved', { actor, from, to })
  return row.to
    ? phrase('event.movedTo', { actor, to })
    : phrase('event.movedOut', { actor, from })
}

const REVIEW_ICONS = {
  APPROVED: ['check', 'open'],
  CHANGES_REQUESTED: ['file-diff', 'closed'],
}

/** Per event kind: (row, actor) → [icon, icon class, sentence]. */
const DESCRIBE = {
  assigned: assignment('assigned'),
  unassigned: assignment('unassigned'),
  labels: (row, actor) => ['tag', '', labelsPhrase(row, actor)],
  moved: (row, actor) => ['project', '', movedPhrase(row, actor)],
  milestoned: (row, actor) => [
    'milestone',
    '',
    phrase('event.milestoned', { actor, milestone: strong(row.milestone) }),
  ],
  demilestoned: (row, actor) => [
    'milestone',
    '',
    phrase('event.demilestoned', { actor, milestone: strong(row.milestone) }),
  ],
  closed: (row, actor) => [
    row.reason === 'NOT_PLANNED' ? 'skip' : 'issue-closed',
    'done',
    phrase(CLOSED[row.reason] || 'event.closed', { actor }),
  ],
  reopened: (row, actor) => [
    'issue-reopened',
    'open',
    phrase('event.reopened', { actor }),
  ],
  merged: (row, actor) => [
    'git-merge',
    'done',
    phrase('event.merged', { actor }),
  ],
  renamed: (row, actor) => [
    'pencil',
    '',
    phrase('event.renamed', {
      actor,
      from: m('del', row.from),
      to: strong(row.to),
    }),
  ],
  referenced: (row, actor) => [
    'cross-reference',
    '',
    phrase('event.referenced', { actor, source: sourceLink(row.source) }),
  ],
  reviewed: (row, actor) => [
    ...(REVIEW_ICONS[row.state] || ['file-diff', '']),
    phrase(REVIEWED[row.state], { actor }),
  ],
}

/** [icon, icon class, sentence] for a row, or null for kinds not shown. */
function describe(row) {
  const describeKind = DESCRIBE[row.kind]
  return describeKind
    ? describeKind(row, strong(row.actor?.login || 'ghost'))
    : null
}

/** One history line between the comments, like GitHub's timeline. */
export function eventRowView({ key, row }) {
  const described = describe(row)
  if (!described) return null
  const [name, cls, text] = described
  return m('.event', { key }, [
    m('.event-icon', { class: cls }, icon(name)),
    m('.event-text', [
      row.actor && avatarView({ user: row.actor, size: 20 }),
      text,
      m(
        'span.muted.event-time',
        { title: new Date(row.at).toLocaleString(language) },
        timeAgo(row.at)
      ),
    ]),
  ])
}
